import React from 'react';
import { Header } from '../../components/Header';
import { Footer } from '../../components/Footer';
import { Button } from '../../components/ui/Button';
import { getProgramById } from '../../data/programs';
import { Brain, Trophy, Heart, TrendingUp } from 'lucide-react';
export function BrazilianJiuJitsuPage() {
  const program = getProgramById('brazilian-jiu-jitsu');
  if (!program) {
    return null;
  }
  return <div className="min-h-screen bg-white font-sans text-slate-900" data-id="element-1016">
      <Header data-id="element-1017" />

      <main className="pt-20" data-id="element-1018">
        {/* Hero Section with Background Image */}
        <section className="relative py-24 md:py-32 overflow-hidden" data-id="element-1019">
          <div className="absolute inset-0 z-0" data-id="element-1020">
            <img src={program.image} alt={program.name} className="w-full h-full object-cover" data-id="element-1021" />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-slate-900/75" data-id="element-1022"></div>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1023">
            <div className="max-w-3xl" data-id="element-1024">
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight" data-id="element-1025">
                {program.name}
              </h1>
              <p className="text-xl text-slate-200 mb-8 leading-relaxed" data-id="element-1026">
                Master the art of leverage, technique, and strategy. Learn the
                world's most effective ground-fighting system from experienced
                instructors.
              </p>
              <Button variant="primary" size="lg" className="text-lg" data-id="element-1027">
                Begin Your BJJ Journey
              </Button>
            </div>
          </div>
        </section>

        {/* Benefits Grid */}
        <section className="py-16 bg-slate-50" data-id="element-1028">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1029">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-1030">
              <div className="text-center" data-id="element-1031">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#dc2626] rounded-full mb-4" data-id="element-1032">
                  <Brain className="w-8 h-8 text-white" data-id="element-1033" />
                </div>
                <h3 className="text-lg font-bold text-[#1a2332] mb-2" data-id="element-1034">
                  Chess on the Mat
                </h3>
                <p className="text-slate-600" data-id="element-1035">
                  Develop strategic thinking and problem-solving skills
                </p>
              </div>

              <div className="text-center" data-id="element-1036">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#dc2626] rounded-full mb-4" data-id="element-1037">
                  <Trophy className="w-8 h-8 text-white" data-id="element-1038" />
                </div>
                <h3 className="text-lg font-bold text-[#1a2332] mb-2" data-id="element-1039">
                  Proven Effectiveness
                </h3>
                <p className="text-slate-600" data-id="element-1040">
                  Learn techniques that work regardless of size or strength
                </p>
              </div>

              <div className="text-center" data-id="element-1041">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#dc2626] rounded-full mb-4" data-id="element-1042">
                  <Heart className="w-8 h-8 text-white" data-id="element-1043" />
                </div>
                <h3 className="text-lg font-bold text-[#1a2332] mb-2" data-id="element-1044">
                  Full-Body Workout
                </h3>
                <p className="text-slate-600" data-id="element-1045">
                  Build strength, endurance, and flexibility naturally
                </p>
              </div>

              <div className="text-center" data-id="element-1046">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#dc2626] rounded-full mb-4" data-id="element-1047">
                  <TrendingUp className="w-8 h-8 text-white" data-id="element-1048" />
                </div>
                <h3 className="text-lg font-bold text-[#1a2332] mb-2" data-id="element-1049">
                  Clear Progression
                </h3>
                <p className="text-slate-600" data-id="element-1050">
                  Structured belt system with measurable skill development
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16 bg-white" data-id="element-1051">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1052">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a2332] mb-6" data-id="element-1053">
              The Gentle Art That Levels the Playing Field
            </h2>

            <div className="prose prose-lg max-w-none text-slate-700 space-y-6" data-id="element-1054">
              <p data-id="element-1055">
                Brazilian Jiu-Jitsu is often called "the gentle art," but don't
                let that fool you. It's one of the most effective martial arts
                systems ever developed, proven time and again in real-world
                situations and competitive arenas worldwide. What makes BJJ
                unique is its emphasis on technique and leverage over brute
                strength—meaning a smaller, weaker person can successfully
                defend against a larger, stronger opponent.
              </p>

              <p data-id="element-1056">
                If you've ever wanted to learn a martial art that actually
                works, BJJ is it. Unlike striking-based martial arts where you
                practice techniques in the air, in Brazilian Jiu-Jitsu you test
                everything through live sparring (called "rolling"). This means
                you'll know exactly what works and what doesn't, building
                genuine skill and confidence that comes from real experience,
                not theory.
              </p>

              <p data-id="element-1057">
                The mental challenge is what keeps people hooked. BJJ is often
                compared to physical chess—you're constantly solving problems,
                anticipating your opponent's moves, and executing strategies
                under pressure. Every roll is different, every training partner
                presents new challenges, and there's always something new to
                learn. This keeps your mind sharp and engaged in ways that
                traditional workouts simply can't match.
              </p>

              <p data-id="element-1058">
                The physical benefits are remarkable. You'll develop functional
                strength, improve cardiovascular endurance, increase flexibility
                and body awareness, and burn calories like crazy—all while
                having so much fun you'll forget you're working out. Students
                often tell us they're in the best shape of their lives after
                training BJJ, and they got there by doing something they
                genuinely enjoy rather than forcing themselves through boring
                gym routines.
              </p>

              <p data-id="element-1059">
                Our BJJ program welcomes everyone from complete beginners to
                experienced grapplers. We offer both Gi (traditional uniform)
                and No-Gi classes, giving you a well-rounded skill set. Our
                instructors break down complex techniques into manageable steps,
                ensuring you build a solid foundation before progressing to
                advanced concepts. The structured belt system provides clear
                goals and recognition for your hard work and dedication.
              </p>

              <p data-id="element-1060">
                Perhaps the best part of training BJJ is the community. There's
                something special about the bonds formed through training
                together, pushing each other to improve, and celebrating each
                other's progress. The mutual respect and camaraderie in our BJJ
                classes create an environment where everyone supports each
                other's growth, regardless of belt level or experience.
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-[#dc2626] to-[#b91c1c] text-white" data-id="element-1061">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1062">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1063">
              Experience the Power of Brazilian Jiu-Jitsu
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto" data-id="element-1064">
              Join our thriving BJJ community and discover why this martial art
              has transformed millions of lives worldwide. Start with 2 free
              weeks and see what makes Brazilian Jiu-Jitsu so addictive.
            </p>
            <Button variant="secondary" size="lg" className="text-lg shadow-xl" data-id="element-1065">
              Claim Your 2 Free Weeks
            </Button>
            <p className="text-sm text-white/80 mt-4" data-id="element-1066">
              Gi and No-Gi classes available • All skill levels welcome • Open
              mat sessions
            </p>
          </div>
        </section>
      </main>

      <Footer data-id="element-1067" />
    </div>;
}